import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { 
  Plus, 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  ChevronRight,
  RefreshCw,
  LogOut,
  Wifi,
  WifiOff,
  Globe
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/v2';

/**
 * MobileRUHApp - Hovedkomponent for mobil RUH PWA
 * Optimalisert for rask registrering av uønskede hendelser
 */
export default function MobileRUHApp() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';
  
  const [ruhList, setRuhList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load RUH list
  useEffect(() => {
    if (user) {
      fetchRUHList();
      checkPendingRUH();
    }
  }, [user]);

  const fetchRUHList = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/deviations?type=ruh`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRuhList(response.data.deviations || []);
    } catch (error) {
      console.error('Failed to fetch RUH list:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const checkPendingRUH = () => {
    const pending = JSON.parse(localStorage.getItem('pending_ruh') || '[]');
    setPendingCount(pending.length);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRUHList();
  };

  const handleLogout = () => {
    logout();
    navigate('/m/ruh/login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'in_progress': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'closed': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertTriangle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'closed': return <CheckCircle2 className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: isNorwegian ? 'Åpen' : 'Open',
      in_progress: isNorwegian ? 'Under arbeid' : 'In progress',
      closed: isNorwegian ? 'Lukket' : 'Closed'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(isNorwegian ? 'nb-NO' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleLanguage = () => {
    const newLang = isNorwegian ? 'en' : 'nb';
    i18n.changeLanguage(newLang);
    localStorage.setItem('systemklar_language', newLang);
  };

  if (!user) {
    navigate('/m/ruh/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="mobile-ruh-app">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900 border-b border-slate-800 safe-area-top">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Logo - same as main app */}
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklar" 
                className="h-7"
              />
              <div>
                <h1 className="text-lg font-bold text-white">RUH</h1>
                <p className="text-xs text-slate-500">{user?.name || 'Bruker'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Language toggle */}
              <button
                onClick={toggleLanguage}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="language-toggle"
              >
                <Globe className="w-5 h-5" />
              </button>
              {/* Online status */}
              <div className={`p-2 rounded-lg ${isOnline ? 'text-emerald-400' : 'text-rose-400'}`}>
                {isOnline ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
              </div>
              {/* Refresh */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              {/* Logout */}
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Pending sync banner */}
        {pendingCount > 0 && (
          <div className="px-4 py-2 bg-amber-500/10 border-t border-amber-500/20">
            <p className="text-xs text-amber-400 text-center">
              {pendingCount} {isNorwegian ? 'rapport(er) venter på synkronisering' : 'report(s) waiting to sync'}
            </p>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto pb-24">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
          </div>
        ) : ruhList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 px-8 text-center">
            <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-slate-600" />
            </div>
            <p className="text-slate-400 mb-2">
              {isNorwegian ? 'Ingen RUH-rapporter ennå' : 'No RUH reports yet'}
            </p>
            <p className="text-sm text-slate-500">
              {isNorwegian ? 'Trykk + for å registrere en hendelse' : 'Tap + to register an incident'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {ruhList.map((ruh) => (
              <button
                key={ruh.id}
                onClick={() => navigate(`/m/ruh/${ruh.id}`)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-left hover:border-slate-700 transition-colors"
                data-testid={`ruh-item-${ruh.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">
                      {ruh.title || ruh.description?.slice(0, 50) || 'RUH'}
                    </p>
                    <p className="text-sm text-slate-400 mt-1 line-clamp-2">
                      {ruh.description}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(ruh.status)}`}>
                        {getStatusIcon(ruh.status)}
                        {getStatusLabel(ruh.status)}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatDate(ruh.created_at)}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-600 flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Floating action button */}
      <button
        onClick={() => navigate('/m/ruh/new')}
        className="fixed bottom-6 right-6 w-14 h-14 bg-cyan-500 rounded-full shadow-lg shadow-cyan-500/25 flex items-center justify-center text-slate-950 hover:bg-cyan-400 active:scale-95 transition-all safe-area-bottom"
        data-testid="new-ruh-btn"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Bottom safe area spacer */}
      <div className="h-safe-area-bottom" />
    </div>
  );
}
